import React, { useEffect, useRef, useState } from "react";
import { buttonStyle, MainContainer, SideContent, ToggleStrip } from "./styled";
import TreeView from "../TreeView";
import { Grid, Alert, Button as MuiButton } from "@mui/material";
import Button from "../Button";
import DialogBox from "../DialogBox";
import { locale } from "../../utils/LocaleManager";
import TextFieldInput from "../Textfield";
import { useFolderStore } from "../../stores/folderStore";
import { newFolder } from "../../services/folderServices";
import { newFolderRequest } from "../../models/folder";
import { Bounce, toast } from "react-toastify";
import { useRolePermissionsStore } from "../../stores/rolePermissionsStore";
import { handleError } from "../../utils/utilFunctions";
import { useVerticalBrowseStore } from "../../stores/verticalBrowseStore";
import BrowseModeToggle from "./BrowseModeToggle";
import VerticalsContent from "./VerticalsContent";

const SideFolderContainer = ({
  isClickedBreadCrumb,
  setIsClickedBreadCrumb,
  isClickedFolder,
  setIsClickedFolder,
  verticalBrowseAvailable = false,
  onBrowseNavigate,
}: {
  isClickedBreadCrumb: any;
  setIsClickedBreadCrumb: any;
  isClickedFolder: any;
  setIsClickedFolder: any;
  verticalBrowseAvailable?: boolean;
  onBrowseNavigate?: (path: string) => void;
}) => {
  const mode = useVerticalBrowseStore((state: any) => state.mode);
  const browseAvailability = useVerticalBrowseStore((state: any) => state.browseAvailability);
  const verticalManagementCapability = useRolePermissionsStore(
    (state: any) => state.rolePermissions?.verticalManagement === true,
  );

  const showBrowseControls =
    verticalBrowseAvailable && browseAvailability === "allowed";
  const showAvailabilityError = browseAvailability === "error";
  const retryButtonRef = useRef<HTMLButtonElement | null>(null);
  const pendingRetryFocus = useRef(false);

  useEffect(() => {
    if (showAvailabilityError && pendingRetryFocus.current && retryButtonRef.current) {
      retryButtonRef.current.focus();
      pendingRetryFocus.current = false;
    }
  }, [showAvailabilityError]);

  const retryBrowseAvailability = () => {
    pendingRetryFocus.current = retryButtonRef.current?.contains(document.activeElement) ?? false;
    useVerticalBrowseStore.getState().retryBrowseAvailability();
  };

  return (
    <MainContainer id="side-folder-rail">
      {showBrowseControls && (
        <ToggleStrip>
          <BrowseModeToggle
            verticalBrowseAvailable={verticalBrowseAvailable}
            onNavigate={onBrowseNavigate}
          />
        </ToggleStrip>
      )}
      {showAvailabilityError && (
        <AvailabilityErrorAlert
          retryButtonRef={retryButtonRef}
          onRetry={retryBrowseAvailability}
        />
      )}
      <SideContent>
        {mode === "verticals" && showBrowseControls ? (
          <VerticalsContent
            verticalManagementCapability={verticalManagementCapability}
            onNavigate={onBrowseNavigate}
          />
        ) : (
          <FoldersContent
            isClickedBreadCrumb={isClickedBreadCrumb}
            setIsClickedBreadCrumb={setIsClickedBreadCrumb}
            isClickedFolder={isClickedFolder}
            setIsClickedFolder={setIsClickedFolder}
          />
        )}
      </SideContent>
    </MainContainer>
  );
};

const AvailabilityErrorAlert = ({
  retryButtonRef,
  onRetry,
}: {
  retryButtonRef: React.RefObject<HTMLButtonElement>;
  onRetry: () => void;
}) => {
  return (
    <Alert
      role="alert"
      severity="error"
      sx={{ m: 2 }}
      action={
        <MuiButton
          color="inherit"
          size="small"
          ref={retryButtonRef}
          onClick={onRetry}
        >
          Retry
        </MuiButton>
      }
    >
      Failed to check vertical availability.
    </Alert>
  );
};

const FoldersContent = ({
  isClickedBreadCrumb,
  setIsClickedBreadCrumb,
  isClickedFolder,
  setIsClickedFolder,
}: {
  isClickedBreadCrumb: any;
  setIsClickedBreadCrumb: any;
  isClickedFolder: any;
  setIsClickedFolder: any;
}) => {
  const rolePermissions = useRolePermissionsStore(
    (state: any) => state.rolePermissions,
  );
  const isShowNewFolder = useFolderStore((state: any) => state.isShowNewFolder);
  const setIsShowNewFolder = useFolderStore(
    (state: any) => state.setIsShowNewFolder,
  );
  const folderId = useFolderStore((state: any) => state.folderId);
  const setIsCreateSuccess = useFolderStore(
    (state: any) => state.setIsCreateSuccess,
  );
  const [folderName, setFolderName] = useState("");
  const [newFolderError, setNewFolderError] = useState("");
  const [inlineError, setInlineError] = useState<string>("");

  const fetchNewFolder = async (request: newFolderRequest) => {
    try {
      setNewFolderError("");
      setInlineError("");
      const newFolderAPI = await newFolder(request);
      if (newFolderAPI?.status === 200) {
        setIsShowNewFolder(false);
        setFolderName("");
        setIsCreateSuccess(true);
        toast.success(`${folderName} folder was successfully created`, {
          position: "bottom-left",
          autoClose: 2000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          pauseOnFocusLoss: false,
          draggable: true,
          progress: undefined,
          theme: "colored",
          transition: Bounce,
          onClose: async () => {
            setIsCreateSuccess(false);
          },
        });
      } else {
        const errorResponse = newFolderAPI?.response;
        const status = errorResponse?.status;
        const data = errorResponse?.data;

        if (status === 403) {
          setIsShowNewFolder(false);
          setFolderName("");
          setNewFolderError("");
          handleError({
            status,
            toast,
            errorMessage: "",
            onClose: () => {},
          });
          return;
        }

        if (status === 401) {
          handleError({
            status,
            toast,
            errorMessage: "",
            onClose: () => {},
          });
          return;
        }

        if (status === 200 || status === 400 || status === 409) {
          if (data?.content?.includes("exists")) {
            setNewFolderError("Folder already exists.");
            return;
          }
        }

        handleError({
          status,
          toast,
          errorMessage: "",
          onClose: () => {},
        });
        setInlineError(`Failed to create folder. Please try again.`);
      }
    } catch (error) {
      console.error("Error creating new folders:", error);
      handleError({
        status: 0,
        toast,
        errorMessage: `Error creating new folders: ${error}`,
        onClose: () => {},
      });
      setInlineError(`Failed to create folder. Please try again.`);
    }
  };

  const handleRetry = async () => {
    setInlineError("");
    await createFolder();
  };

  const renderCreateDialog = () => {
    return (
      <div data-testid="new-folder-name-field" style={{ marginTop: "16px" }}>
        <TextFieldInput
          label="Folder name"
          value={folderName}
          onChange={(e: any) => setFolderName(e.target.value)}
          autoFocus={true}
          error={newFolderError?.length > 0}
          helperText={newFolderError}
        />
      </div>
    );
  };

  const onClickNewFolder = () => {
    setIsShowNewFolder(true);
  };

  const createFolder = () => {
    if (!folderName || (folderId === 1 && !rolePermissions?.folder?.root) || !rolePermissions?.folder?.add) return;

    const request: newFolderRequest = {
      name: folderName,
      parentFolderId: folderId === 1 ? null : folderId,
    };
    return fetchNewFolder(request);
  };

  return (
    <>
      <Grid container>
        {rolePermissions?.folder?.root && rolePermissions.folder.add && (
          <Grid item xs={12}>
            <Button
              variant="variant"
              size="large"
              color=""
              style={buttonStyle}
              onClick={onClickNewFolder}
            >
              + New Folder
            </Button>
          </Grid>
        )}
        <TreeView
          isClickedBreadCrumb={isClickedBreadCrumb}
          setIsClickedBreadCrumb={setIsClickedBreadCrumb}
          isClickedFolder={isClickedFolder}
          setIsClickedFolder={setIsClickedFolder}
        />
      </Grid>

      {isShowNewFolder ? (
        <DialogBox
          header={locale.new_folder}
          body={
            <>
              {renderCreateDialog()}
              {inlineError && (
                <Alert
                  severity="error"
                  sx={{ mt: 2 }}
                  action={
                    <MuiButton color="inherit" size="small" onClick={handleRetry}>
                      Retry
                    </MuiButton>
                  }
                >
                  {inlineError}
                </Alert>
              )}
            </>
          }
          subBody={""}
          onCancel={() => {
            setIsShowNewFolder(false);
            setNewFolderError("");
            setFolderName("");
            setInlineError("");
          }}
          onSubmit={() => createFolder()}
          isDelete={false}
          primaryButton={locale.ok}
          secondaryButton={locale.cancel}
          disablePrimary={
            !folderName ||
            (folderId === 1 && !rolePermissions?.folder?.root) ||
            !rolePermissions?.folder?.add
          }
        />
      ) : (
        <></>
      )}
    </>
  );
};


export default SideFolderContainer;
